import { useCallback, useEffect, useState } from 'react';
import { Smartphone, ShieldCheck, Loader2, KeyRound, Clock } from 'lucide-react';
import { api } from '../../lib/api';
import { StatusBadge } from '../../components/StatusBadge';
import { Interruptor } from '../../components/Interruptor';
import { useDialog } from '../../components/DialogProvider';
import LogUsoAppCel from './LogUsoAppCel';

// Configuracoes → AppCel: manutencao das contas do aplicativo do cliente.
//
// ⚠️ **Administrador AQUI nao e' admin do ERP** (migration 0224). A unica coisa que
// o papel decide e' se a opcao "Entrar como loja" aparece no menu ☰ daquela pessoa
// dentro do aplicativo. Quem passa por aquela porta continua sendo decidido pelo
// login do sistema e pelo device-trust — por isso o rodape da tela diz isso em voz
// alta, em vez de deixar "Administrador" sugerir poder que ele nao da.
//
// A mesma lista existe no ☰ da visao da loja (`atender/UsuariosApp.tsx`), pro caso
// de estar com o celular na mao. As duas leem a MESMA rota; o que muda e' a moldura.
//
// ⚠️ **O "Resetar senha" (02/09/2026) esta SO aqui, e isso foi escolha dele.** A
// outra tela le a mesma rota e ganharia o botao de graca; ficou de fora porque o
// reset e' conversa — a loja confirma quem esta pedindo — e isso se faz no balcao,
// nao no celular. Se um dia precisar la, e' copiar a celula.
//
// ⛔ **O botao NAO entrega senha nenhuma**: ele abre uma janela de 2h em que aquela
// conta aceita criar senha nova pelo "Esqueci minha senha" do aplicativo. Desenho
// dele: *"eu resetar a senha pelo botao e o usuario criar a definitiva no primeiro
// login"* — assim ninguem dita codigo por telefone e a loja nunca sabe a senha do
// cliente. O prazo curto e' o que impede que a conta fique alcancavel a noite
// inteira por quem souber o telefone (o endereco `/app` toda conta ja conhece).

interface UsuarioApp {
  id: number;
  nome: string;
  telefone: string;
  papel: 'usuario' | 'administrador';
  ativo: boolean;
  tem_ficha: boolean;
  criado_em: string;
  ultimo_acesso_em: string | null;
  /** Reset em curso: ate quando a conta aceita criar senha nova sem saber a antiga.
   *  ⚠️ Vem do servidor JA filtrado pelo relogio do banco — janela vencida chega
   *  `null`, e nao uma data no passado que esta tela teria que comparar com o
   *  relogio da maquina do balcao. */
  senha_redefinir_ate: string | null;
}

function telefoneBonito(t: string): string {
  const d = t.replace(/\D/g, '');
  const semDdi = d.startsWith('55') ? d.slice(2) : d;
  if (semDdi.length < 10) return t;
  const resto = semDdi.slice(2);
  const meio = resto.length === 9 ? 5 : 4;
  return `(${semDdi.slice(0, 2)}) ${resto.slice(0, meio)}-${resto.slice(meio)}`;
}

function dataHora(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/** O que o reset devolve: a janela aberta, nunca uma senha. */
interface ResetAberto {
  id: number;
  nome: string;
  senha_redefinir_ate: string;
}

/** `11:32` — a hora em que a janela fecha, que e' o que ele precisa dizer ao cliente. */
function horaDe(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function UsuariosAppCel() {
  const dialog = useDialog();
  /**
   * ⚠️ **Usuarios ABRE, e isso e' decisao, nao ordem de escrita** ([DA-627] p.4: a 1a
   * aba e' a que abre, e abrir decide o que a pessoa encontra sem procurar). Esta tela
   * existe pra administrar conta — resetar senha, promover a administrador —, e o log
   * e' consulta. Nao persiste em `localStorage` de proposito: quem vem aqui as pressas,
   * com o cliente no telefone, precisa da lista.
   */
  const [aba, setAba] = useState<'usuarios' | 'log'>('usuarios');
  const [lista, setLista] = useState<UsuarioApp[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState<number | null>(null);
  /**
   * O ultimo reset aberto nesta sessao de tela.
   *
   * ⚠️ **A verdade mora na LISTA** (`senha_redefinir_ate`, que o servidor ja
   * devolve filtrado pelo relogio do banco) — este estado so evita reler a lista
   * inteira depois de um clique. Recarregando a pagina, o pendente continua
   * aparecendo: janela aberta e' estado do sistema, nao lembranca desta aba.
   */
  const [ultimoReset, setUltimoReset] = useState<ResetAberto | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLista(await api.get<UsuarioApp[]>('/api/app-usuarios'));
      setErro(null);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui carregar a lista agora.');
    }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  async function trocarPapel(u: UsuarioApp) {
    setGravando(u.id);
    setErro(null);
    try {
      const alt = await api.patch<{ id: number; papel: UsuarioApp['papel'] }>(
        `/api/app-usuarios/${u.id}/papel`,
        { papel: u.papel === 'administrador' ? 'usuario' : 'administrador' },
      );
      // ⚠️ **Atualiza SO a linha, e nao relê a lista** — a ordenacao do servidor poe
      // administrador em cima, entao recarregar aqui fazia a linha recem-promovida
      // SALTAR pro topo e outra pessoa ocupar o lugar embaixo do cursor. O Ruan
      // relatou o efeito exato: *"so consigo ativar meu usuario como admin se
      // habilitar todos os outros antes de mim"* — cada clique seguinte caia em quem
      // tinha subido uma posicao.
      //
      // Regra que generaliza: **lista nao se reordena pelo campo que esta sendo
      // editado enquanto se edita.** A ordem serve a leitura, no carregamento; durante
      // a edicao ela precisa ficar parada, senao o alvo do proximo clique muda sozinho.
      // Familia da [DA-350] (estado indexado por posicao mente assim que a lista muda).
      //
      // O valor vem do que o servidor DEVOLVEU, nao do que a tela pediu.
      setLista((atual) => atual?.map((x) => (x.id === alt.id ? { ...x, papel: alt.papel } : x)) ?? atual);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui mudar o papel.');
    } finally {
      setGravando(null);
    }
  }

  /**
   * Liga/desliga o acesso daquela conta ao aplicativo.
   *
   * ⚠️ **So o DESLIGAR pergunta.** Confirmar tambem o religar encheria de dialogo o
   * gesto inofensivo (devolver acesso a quem esta do outro lado do telefone) e faria
   * o operador clicar em "Sim" no automatico — que e' o oposto do que a confirmacao
   * do desligar precisa provocar.
   *
   * O texto diz as duas metades, no molde do reset: o que acontece AGORA (ela cai na
   * hora) e o que NAO acontece (a conta nao e' apagada).
   */
  async function trocarAtivo(u: UsuarioApp) {
    if (u.ativo) {
      const ok = await dialog.confirmar(
        `Tirar o acesso de ${u.nome} ao aplicativo?\n\n`
        + 'Essa pessoa e desconectada na hora: na proxima vez que abrir o app, cai na tela de sessao '
        + 'encerrada e a senha dela deixa de valer.\n\n'
        + 'A conta nao e apagada — cadastro, notas e conversas ficam, e religar aqui devolve o acesso.',
        { titulo: 'Tirar acesso ao AppCel', textoConfirmar: 'Tirar acesso' },
      );
      if (!ok) return;
    }

    setGravando(u.id);
    setErro(null);
    try {
      const alt = await api.patch<{ id: number; ativo: boolean }>(
        `/api/app-usuarios/${u.id}/ativo`, { ativo: !u.ativo },
      );
      // Mesma lição do `trocarPapel`: atualiza SO a linha. Reler faria a lista se
      // reordenar debaixo do cursor e o proximo clique cair em outra pessoa.
      setLista((atual) => atual?.map((x) => (x.id === alt.id ? { ...x, ativo: alt.ativo } : x)) ?? atual);
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui mudar o acesso.');
    } finally {
      setGravando(null);
    }
  }

  /** Janela aberta desta linha, se houver — a lista manda, o clique so adianta. */
  function pendente(u: UsuarioApp): string | null {
    if (ultimoReset?.id === u.id) return ultimoReset.senha_redefinir_ate;
    return u.senha_redefinir_ate;
  }

  async function resetarSenha(u: UsuarioApp) {
    // ⚠️ A confirmacao diz o que o botao FAZ e o que ele NAO faz. As duas metades
    // importam: sem a primeira ele espera receber uma senha aqui; sem a segunda,
    // acha que acabou de derrubar o cliente do aplicativo.
    const ok = await dialog.confirmar(
      `Liberar senha nova para ${u.nome}?\n\n`
      + 'Essa pessoa vai poder abrir o aplicativo, tocar em "Esqueci minha senha", digitar o número dela '
      + 'e escolher a senha que quiser. A liberação vale por 2 horas.\n\n'
      + 'A senha atual continua funcionando e ninguém é desconectado até ela criar a nova.',
      { titulo: 'Resetar senha do AppCel', textoConfirmar: 'Liberar' },
    );
    if (!ok) return;

    setGravando(u.id);
    setErro(null);
    try {
      const r = await api.post<ResetAberto>(`/api/app-usuarios/${u.id}/resetar-senha`);
      setUltimoReset(r);
      // A lista volta com o `senha_redefinir_ate` do servidor: assim o pendente
      // sobrevive a um F5, e a hora que a tela mostra e' a do BANCO, nao a soma que
      // o navegador faria com o relogio da maquina.
      await carregar();
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui liberar a senha nova.');
    } finally {
      setGravando(null);
    }
  }

  async function cancelarReset(u: UsuarioApp) {
    setGravando(u.id);
    setErro(null);
    try {
      await api.delete(`/api/app-usuarios/${u.id}/resetar-senha`);
      setUltimoReset((atual) => (atual?.id === u.id ? null : atual));
      await carregar();
    } catch (e) {
      setErro((e as Error).message || 'Nao consegui cancelar.');
    } finally {
      setGravando(null);
    }
  }

  const admins = lista?.filter((u) => u.papel === 'administrador').length ?? 0;
  const semAcesso = lista?.filter((u) => !u.ativo).length ?? 0;

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <Smartphone size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          AppCel
          {lista && <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.75 }}> ({lista.length})</span>}
        </h2>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '2px solid var(--primary)', marginBottom: 12 }}>
        <Aba ativa={aba === 'usuarios'} onClick={() => setAba('usuarios')}>
          Usuários{lista ? ` (${lista.length})` : ''}
        </Aba>
        <Aba ativa={aba === 'log'} onClick={() => setAba('log')}>Log de uso</Aba>
      </div>

      {aba === 'log' && <LogUsoAppCel />}

      {aba === 'usuarios' && (
        <>
      {erro && (
        <div style={{ backgroundColor: '#ef4444', color: '#000', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14 }}>
          {erro}
        </div>
      )}

      {!lista ? (
        <p style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-color)' }}>
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </p>
      ) : (
        <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
          <table className="tabela" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>NOME</th>
                <th style={{ textAlign: 'left' }}>TELEFONE</th>
                <th style={{ textAlign: 'left' }}>FICHA</th>
                <th style={{ textAlign: 'left' }}>CADASTRO</th>
                <th style={{ textAlign: 'left' }}>ULTIMO ACESSO</th>
                <th style={{ textAlign: 'left' }}>ACESSO AO APP</th>
                <th style={{ textAlign: 'left' }}>ADMINISTRADOR</th>
                <th style={{ textAlign: 'left' }}>SENHA</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.papel === 'administrador' && (
                      <ShieldCheck size={14} style={{ display: 'inline', marginRight: 5, marginBottom: -2, color: 'var(--primary)' }} />
                    )}
                    {u.nome}
                  </td>
                  <td>{telefoneBonito(u.telefone)}</td>
                  {/* Cliente do app nem sempre tem ficha na base fiscal — o vinculo so
                      nasce quando o telefone casa com quem ja existe. */}
                  <td><StatusBadge ativo={u.tem_ficha} labelAtivo="Vinculada" labelInativo="Sem ficha" /></td>
                  <td>{dataHora(u.criado_em)}</td>
                  <td>{dataHora(u.ultimo_acesso_em)}</td>
                  <td>
                    {/* A coluna que faltava: quem tem acesso HOJE. Sem ela, a unica
                        forma de saber se uma conta estava desligada era consultar o
                        banco — a lista mostrava todo mundo igual. */}
                    <Interruptor
                      ligado={u.ativo}
                      onChange={() => void trocarAtivo(u)}
                      desabilitado={gravando === u.id}
                      rotuloLigado="Ativo"
                      rotuloDesligado="Sem acesso"
                    />
                  </td>
                  <td>
                    {/* So a linha em gravacao trava (`gravando === u.id`). Travar a
                        tabela inteira fazia toda chave parecer inerte por um instante
                        — e chave que nao responde convida ao segundo clique, que era
                        metade do bug relatado. */}
                    <Interruptor
                      ligado={u.papel === 'administrador'}
                      onChange={() => void trocarPapel(u)}
                      desabilitado={gravando === u.id}
                      rotuloLigado="Administrador"
                      rotuloDesligado="Usuario"
                    />
                  </td>
                  <td>
                    {pendente(u) ? (
                      // O estado ocupa o lugar do botao, na propria linha — assim nao
                      // ha como ler o "aguardando" de uma pessoa achando que e' de
                      // outra. E a hora e' o que ele diz ao cliente no telefone.
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          fontSize: 12.5, fontWeight: 700, padding: '4px 9px', borderRadius: 6,
                          // Amarelo positivo da casa: verde/vermelho nao servem de
                          // sinal aqui (daltonismo).
                          backgroundColor: '#fde68a', color: '#000', whiteSpace: 'nowrap',
                        }}>
                          <Clock size={12} style={{ display: 'inline', marginRight: 4, marginBottom: -2 }} />
                          Aguardando até {horaDe(pendente(u)!)}
                        </span>
                        <button
                          type="button"
                          onClick={() => void cancelarReset(u)}
                          disabled={gravando === u.id}
                          title="Fechar a liberação agora"
                          style={{ fontSize: 12.5, color: 'var(--text-color)', opacity: gravando === u.id ? 0.4 : 0.8, textDecoration: 'underline' }}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void resetarSenha(u)}
                        disabled={gravando === u.id}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          height: 30, padding: '0 10px', borderRadius: 7,
                          border: '1px solid var(--border)', backgroundColor: 'transparent',
                          color: 'var(--text-color)', fontSize: 12.5, fontWeight: 600,
                          opacity: gravando === u.id ? 0.5 : 1, whiteSpace: 'nowrap',
                        }}
                      >
                        <KeyRound size={13} /> Resetar senha
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* O RECADO, escrito do jeito que ele vai dizer ao cliente. Sem esta linha o
          "Aguardando" na tabela conta o estado e nao conta o gesto — e quem sabe o
          caminho dentro do app sou eu, não quem está do outro lado do telefone. */}
      {ultimoReset && (
        <div style={{ marginTop: 14, padding: '11px 14px', borderRadius: 9, backgroundColor: '#fde68a', color: '#000', fontSize: 13.5, lineHeight: 1.6, maxWidth: 760 }}>
          <b>{ultimoReset.nome}</b> pode criar a senha nova até <b>{horaDe(ultimoReset.senha_redefinir_ate)}</b>.
          {' '}Diga a essa pessoa: <i>abra o aplicativo, toque em <b>Esqueci minha senha</b>, digite o seu celular e escolha a senha</i>.
          {' '}Passou da hora, é só clicar em Resetar senha de novo.
        </div>
      )}

      {/* O que o interruptor faz, e o que ele NAO faz. Sem esta linha, "Administrador"
          num app de loja le como "pode tudo". */}
      {lista && (
        <p style={{ marginTop: 14, fontSize: 13, color: 'var(--text-color)', opacity: 0.8, lineHeight: 1.6, maxWidth: 760 }}>
          Administrador enxerga a opcao <b>Entrar como loja</b> no menu do aplicativo — e so isso.
          Nao da acesso nenhum ao sistema: para atender de fato ainda e preciso o login do sistema,
          num aparelho com certificado. Hoje sao <b>{admins}</b> de <b>{lista.length}</b> contas.
          A ultima nao pode ser rebaixada, senao a porta fecharia por fora.
          {/* ⚠️ Sem esta linha, o "—" que sobra em duas contas parece o mesmo defeito
              que acabou de ser consertado. Diz o que a coluna mede E ate onde ela
              alcanca — a sonda so existe desde 25/08. */}
          {/* O interruptor de acesso e' o unico da tela que TIRA alguma coisa — a
              linha diz o efeito antes do clique, nao so na confirmacao. */}
          <br />
          <b>Acesso ao app</b> desligado derruba a pessoa na hora e a senha dela para de valer; a conta
          e o historico ficam, e religar devolve o acesso. Use ao desligar quem saiu da loja.
          {semAcesso > 0 && <> Hoje <b>{semAcesso}</b> {semAcesso === 1 ? 'conta esta' : 'contas estao'} sem acesso.</>}
          <br />
          <b>Ultimo acesso</b> e a ultima vez que a pessoa ABRIU o aplicativo, nao a ultima vez que
          digitou a senha — quem entrou pelo convite fica logado por 30 dias. A medicao comecou em
          25/08/2026: quem usou antes disso e nao voltou aparece sem data.
        </p>
      )}
        </>
      )}
    </div>
  );
}

/** Aba da tela. Peca local: sao duas, e nao ha padrao de aba compartilhado na casa. */
function Aba({ ativa, onClick, children }: { ativa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '9px 20px', borderRadius: '8px 8px 0 0',
        backgroundColor: ativa ? 'var(--primary)' : 'transparent',
        color: ativa ? '#fff' : 'var(--text-color)',
        fontWeight: ativa ? 700 : 600, fontSize: 13.5,
      }}
    >
      {children}
    </button>
  );
}
